<template>
  <div>
      <div class="title"> Forces Simulator </div>
      <div class="date"> May 2nd, 2019 </div>

      <div class="content">
        
        <div id="pageContainer">
            <div id="errorBox"></div>
            <div style="display: flex;">
                <div id="inputBoxColumn">
                    <div class="inputBox">
                        <input type="text" class="mathInput" value="F_x = 0">
                    </div>
                    <div class="inputBox">
                        <input type="text" class="mathInput" value="F_y = -m*g">
                    </div>
                    <div class="inputBox">
                        <input type="text" class="mathInput" value="m = 1">
                    </div>
                    <div class="inputBox">
                        <input type="text" class="mathInput" value="v_x0 = 5">
                    </div>
                    <div class="inputBox">
                        <input type="text" class="mathInput" value="v_y0 = 5">
                    </div>
                    <div class="inputBox">
                        <input type="text" class="mathInput" value="x_0 = 0">
                    </div>
                    <div class="inputBox" id="lastInputBox">
                        <input type="text" class="mathInput" value="y_0 = 0">
                    </div>
                    <div style="position: relative">
                        <span class="noselect" id="addColumnButton">
                            +
                        </span>
                    </div>
                </div>
                <div id="canvasContainer" style="border: 1px solid gainsboro"></div>
            </div>
        </div>

        <div>
            <p> &emsp;
                This thing graphs the plot of an second order differential equation with one input, time.
                Picture a projectile, with forces acting on it in the x any y directions. How will it's position
                change over time? This answers that question. 'F_x' and 'F_y' are the forces in the x and y directions respectively,
                with v_x0, v_y0 being initial velocities, and x_0, y_0 being initial positions. It graphs the projectile 
                from `t=0` to `t=20`, with `\Delta t = 0.04`.
            </p>
            <p> &emsp;
                For example, by default `F_y` is `-mg`, the force of gravity, a simple projectile motion problem.
                If you set `F_x = 0` and `F_y = -y`, it describes simple harmonic motion.
                For a weird curve, set `F_x = e^{-x}` and `F_y = -0.5`. These curves remind me of fourier series.
            </p>
            <p> &emsp;
                The possibilites are endless. 
            </p>
            <p> &emsp;
                I made this to help me visualize things in my physics class, but I never finished it by the time the class ended... 
                Just finished implementing it this summer because I hate having unfinished projects. It is still full of bugs, but serves its purpose.
            </p>
        </div>

      </div>
  </div>
</template>

<script>

export default {
  name: 'ForcesSimulator',
  mounted: () => {
    const script = function (p5) {
        p5.setup = () => {
            var canvas_width = 750;
            var canvas_height = 600;
            let canvas = p5.createCanvas(canvas_width, canvas_height);
            canvas.parent("canvasContainer");
            do_js(p5);

            window.MathJax.typesetPromise();
        }
    }

    const P5 = require('p5');
    new P5(script);
  }
}

function do_js(p5) {
var OPERATORS = "^*/+-=";
var OPEN_PAREN = "([{";
var CLOSE_PAREN = ")]}";
var MATH_FUNC = {sin: Math.sin, cos: Math.cos, tan: Math.tan, ln: Math.log, 
    sign: (x) => {
        if (x == 0)
            return 0;
        return Math.sign(x)
    }
    , abs: Math.abs, sqrt: Math.sqrt};

/* ----------------------------------- Begin Utility functions ------------------------------- */
// Returns if string is valid number
function is_number(string) {
    return !isNaN(string);
}

// Returns random color
function random_color() {
    return p5.color(rand_int(0, 255), rand_int(0, 255), rand_int(0, 255));
}

// Returns random in range (inclusive)
function rand_int(start, end) {
    return Math.floor((Math.random() * (end - start + 1)) + start)
}

// Returns if item in iterable
function contains(iter, item) {
    for (let i of iter)
    if (item == i)
    return true;
    return false;
}

// Returns if open and close parenthesis match, e.g. [} -> false, [] -> true
function paren_match(open, close) {
    return OPEN_PAREN.indexOf(open) == CLOSE_PAREN.indexOf(close);
}
/* ----------------------------------- End Utility functions --------------------------------- */


/* ------------------------------------------------------------------------------------------- */
/* ----------------------------------- Start of Parser --------------------------------------- */
/* ------------------------------------------------------------------------------------------- */

// Given some tokens from an infix expression, i.e. ['1', '+', '2'] -> 3
function parse_expression(tokens) {
    let expression = tokens.join(" ");

    // Simple case if just a single token
    if (tokens.length == 1) {
        if (!is_number(tokens[0]))
            throw `Unknown number ${tokens[0]} in expression '${expression}'`
        return parseFloat(tokens[0]);
    }
    
    // Run through tokens and solve all parenthesis first
    for (let a = 0; a < tokens.length; a++) {
        if (contains(OPEN_PAREN, tokens[a])) {
            // Find the correct matching closing parenthesis
            let stack = [tokens[a]];
            for (let b = a + 1; b < tokens.length; b++) {
                let token = tokens[b]
                
                if (contains(OPEN_PAREN, token)) {
                    stack.push(token)
                }
                else if (contains(CLOSE_PAREN, token)) {
                    let open = stack.pop();
                    if (!paren_match(open, token))
                    throw `Invalid parenthesis ${a}:${b} in expression ${tokens.join(" ")}`
                    
                    // We found the correct closing parenthesis at index b
                    if (stack.length == 0) {
                        tokens.splice(a, b - a + 1, parse_expression(tokens.slice(a + 1, b)));
                        break;
                    }
                }
            }
        }
    }
    
    // Guarenteed to have no parenthesis at this point, just do PEMDAS now
    let op_funcs = {};
    op_funcs["+"] = (a, b) => a + b;
    op_funcs["-"] = (a, b) => a - b;
    op_funcs["*"] = (a, b) => a * b;
    op_funcs["/"] = (a, b) => {
        if (b == 0) throw "Cannot divide by 0";
        return a / b;
    };
    op_funcs["^"] = (a, b) => Math.pow(a, b);
    

    var n1;
    var op;
    var n2;

    // Helper function that parses expression that only has 3 tokens and an operator between
    function parse_simple(expr) {
        if (expr.length < 3) throw `Expression '${expression}' missing tokens`;
        if (expr.length > 3) throw `Expression '${expression}' is broken somehow :0`;
        
        n1 = expr[0];
        op = expr[1];
        n2 = expr[2];
        
        if (!(contains(OPERATORS, op))) throw `Unknown operator '${op}' in '${expr}'`;
        if (!is_number(n1)) throw `Unknown number '${n1}' in '${expr}'`;
        if (!is_number(n2)) throw `Unknown number '${n2}' in '${expr}'`;
        
        return op_funcs[op](parseFloat(n1), parseFloat(n2));
    }
    
    // Does the given operation looking from left to right
    function compute_operation(operator) {
        for (let i = 1; i < tokens.length; i++) {
            let token = tokens[i];
            if (token == operator) {
                tokens.splice(i - 1, 3, parse_simple(tokens.slice(i - 1, i + 2)))
                i -= 1;
            }
        }
    }
    
    // First thing we do is compute all of the math functions as defined by math func
    for (let i = 0; i < tokens.length; i++) {
        let token = tokens[i];
        if (token in MATH_FUNC) {
            // If we find a math function, we expect it to be syntaxed in the form f(x)
            // ************* e.g: [... "sin", "(", "2" , "*", "pi", ")", ...]
            // Because we are guarenteed to not have parentheses at this point, it should rather be
            // ************* e.g: [... "sin", "6.28", ...]
            let argument = tokens[i + 1];
            if (!is_number(argument)) 
                throw `Unknown number '${n1}' in function ${token} in expression '${expression}'`;
            
            let function_output = MATH_FUNC[token](parseFloat(argument));
            if (!is_number(function_output)) 
                throw `Error in evaluating ${token}(${argument}) in expression '${expression}'`

            tokens.splice(i, 2, function_output);
        }
    }

    // OPERATORS is defined by order of PEMDAS
    for (let operator of OPERATORS)
        compute_operation(operator);
    
    if (tokens.length != 1)
    throw `Missing operators in expression '${expression}'`;
    
    return tokens[0];
}

function is_token(token_string) {
    if (contains(OPERATORS, token_string)) return false;
    if (contains(OPEN_PAREN, token_string)) return false;
    if (contains(CLOSE_PAREN, token_string)) return false;
    if (token_string in MATH_FUNC) return false;
    
    return true;
}

// Creates a list of tokens from a string, e.g. "1 + 3+v_x" -> ["1", "+", "3", "+", "v_x"]
// e.g: "1 + ln(3)" = ["1", "+", "ln", "(", "3", ")"]
// ***********>    Anything that is not an operator or parenthesis or math function is considered a token
// ***********>    A token is supposed to be a numerical value or substitable variable
// ***********>    Insert '0' to negate, e.g. "-1" -> "0 - 1"
// ***********>    Insert '*' implied if no operator before/after parenthesis, e.g. 1(2) = 1 * (2)
function create_token_list(string) {
    // Remove all whitespace
    string = string.replace(/ /g, '');
    
    let tokens = [];
    
    // Run down the boi and split when we see operator (I mean a token)
    let start = 0;
    for (let i = 0; i < string.length; i++) {
        if (!is_token(string[i])) {
            if (i != start)
            tokens.push(string.slice(start, i));
            
            tokens.push(string[i]);
            start = i + 1;
        }
    }
    if (start < string.length)
    tokens.push(string.slice(start));
    
    // We have now created a token list, touch ups for specific operators
    // Insert 0 before - if a token is not there '1' -> '0 - 1'
    // Insert * if token comes before '(' or after ')'
    for (let i = 0; i < tokens.length; i++) {
        let token = tokens[i];
        
        if (token == '-') {
            if (i == 0) tokens.splice(0, 0, '0');
            else if (!is_token(tokens[i - 1])) tokens.splice(i, 0, '0');
        }
        else if (token == '(' && i > 0) {
            if (is_token(tokens[i - 1]))
            tokens.splice(i, 0, "*");
        }
        else if (token == ')' && i + 1 < tokens.length) {
            if (is_token(tokens[i + 1]))
            tokens.splice(i + 1, 0, "*")
        }
    }
    
    return tokens;
}

// We never actually use this but it computes a given expression
// function compute(expression) {
//     return parse_expression(create_token_list(expression));
// }

/* ------------------------------------------------------------------------------------------- */
/* ----------------------------------- End of Parser ----------------------------------------- */
/* ------------------------------------------------------------------------------------------- */

/* ------------------------------------------------------------------------------------------- */
/* ----------------------------------- Start of Projectile ----------------------------------- */
/* ------------------------------------------------------------------------------------------- */

var symbol_table = {};
symbol_table["g"] = 9.81;
symbol_table["pi"] = Math.PI;
symbol_table["e"] = Math.E;

// Substitutes all tokens that are keys in the symbol table for their value in that table
// Say token_list = ["m", "+", "3"], and m = 3 in the symbol_table
// This function returns the a new token list with "3" substituted for m, e.g. ["3", "+", "3"]
function substitute_known_symbols(token_list) {
    let copy = token_list.slice();
    for (let i = 0; i < copy.length; i++) {
        let token = copy[i];
        if (is_token(token) && !is_number(token)) {
            if (token in symbol_table)
                copy[i] = symbol_table[token]
            else
                throw "Unknown symbol " + token;
        }
    }

    return copy;
}

class Projectile {
    constructor() {
        // The following are floating point initial constants
        this.m = symbol_table["m"];
        this.x = symbol_table["x_0"];
        this.y = symbol_table["y_0"];
        this.v_x = symbol_table["v_x0"];
        this.v_y = symbol_table["v_y0"];
        this.t = 0;

        // expect symbol_table to contain all numerical values except for 'f_x', 'f_y'
        // which will be token lists for the equation, sliced to everyleft right of the '='
        this.update_forces();
    }
    
    update_forces() {
        // this.f_[xy] are token tables for those equations
        this.f_x = symbol_table["F_x"];
        this.f_y = symbol_table["F_y"];
    }

    update_initial_conditions() {
        this.m = symbol_table["m"];
        this.x = symbol_table["x_0"];
        this.y = symbol_table["y_0"];
        this.v_x = symbol_table["v_x0"];
        this.v_y = symbol_table["v_y0"];
    }

    update_symbol_table() {
        symbol_table["m"] = this.m;
        symbol_table["x"] = this.x;
        symbol_table["y"] = this.y;
        symbol_table["v_x"] = this.v_x;
        symbol_table["v_y"] = this.v_y;
        symbol_table["t"] = this.t;
    }

    get force_x() {
        this.update_symbol_table();
        let token_list = substitute_known_symbols(this.f_x);
        return parse_expression(token_list);
    }
    
    get force_y() {
        this.update_symbol_table();
        let token_list = substitute_known_symbols(this.f_y);
        return parse_expression(token_list);
    }

    move(time_step) {
        let a_x = this.force_x * this.m;
        let a_y = this.force_y * this.m;

        this.v_x += a_x * time_step;
        this.v_y += a_y * time_step;

        this.x += this.v_x * time_step;
        this.y += this.v_y * time_step;

        this.t += time_step;
    }

    get_points(time_start, time_end, num_steps) {
        this.update_initial_conditions();
        this.t = time_start;
        let time_step = (time_end - time_start) / num_steps;
        let points = [new Point(this.x, this.y)];

        while (this.t <= time_end) {
            this.move(time_step);
            points.push(new Point(this.x, this.y));
        }

        return points;
    }
}

/* ------------------------------------------------------------------------------------------- */
/* ----------------------------------- End of Projectile ------------------------------------- */
/* ------------------------------------------------------------------------------------------- */

/* ------------------------------------------------------------------------------------------- */
/* ----------------------------------- Start of Grid ----------------------------------------- */
/* ------------------------------------------------------------------------------------------- */

class Grid {
    constructor(x_range, y_range, width, height) {
        this.x_range = x_range;
        this.y_range = y_range;
        this.width = width;
        this.height = height;

        // ppx = "pixels per x"
        this.ppx = 1;
        this.ppy = 1;

        this.graphs = [];
        this.scatter_plots = [];
    }

    draw() {
        p5.clear();
        // Plot this function on desmos to get a sense of it
        let step_func = (x) => {
            let log = (x, b) => Math.log(x) / Math.log(b);
            let base = 10;
            let power_of_ten = Math.pow(base, Math.floor(log(x, base) - 1));
            let max_index = 6;
            let index = Math.floor((log(x, base) - Math.floor(log(x, base))) * max_index);
            let multiplier = index * index + 1; // multipler = f(x) = x^2 + 1 = 1, 2, 5
            
            return power_of_ten * multiplier;
        }

        // x_distance is the length of the x_range
        // num steps is the number of major grid lines
        let x_s = this.x_range[0];
        let x_e = this.x_range[1];
        let y_s = this.y_range[0];
        let y_e = this.y_range[1];
        let x_distance = x_e - x_s;
        let y_distance = y_e - y_s;
        let step_size_x = step_func(x_distance);
        let step_size_y = step_func(y_distance);
        this.ppx = this.width / x_distance;
        this.ppy = this.height / y_distance;

        // Relative to top left coordinates of the origin in terms of px
        let origin_x_px = (-1 * x_s) * this.ppx;
        let origin_y_px = y_e * this.ppy;

        function draw_grid_lines(step_size, step_start, step_end, horizontal, object) {
            let minor_grid = 3;
            for (let i = step_start; i <= step_end; i++) {
                let coord = i * step_size;
                
                if (coord == 0) p5.strokeWeight(2);
                else p5.strokeWeight(1);
                
                // Style for axis numbers
                p5.textSize(11);
                p5.fill(255, 0, 0);
                
                p5.stroke(150);
                if (horizontal) {
                    let y = (-coord + y_e) * object.ppy;
                    p5.line(0, y, object.width, y);
                    
                    // Draw numbers on axis
                    p5.stroke(0, 0, 0, 0);
                    p5.text(coord.toString().slice(0, 5), origin_x_px, y);
                    
                    // Draw minor grid lines
                    p5.strokeWeight(0.5);
                    p5.stroke(150);
                    for (let n = 1; n < minor_grid; n++) {
                        coord += (step_size / minor_grid);
                        y = (-coord + y_e) * object.ppy;
                        p5.line(0, y, object.width, y);
                    }
                }
                else {
                    let x = (coord - x_s) * object.ppx;
                    p5.line(x, 0, x, object.height);
                    
                    // Draw numbers on axis
                    p5.stroke(0, 0, 0, 0);
                    p5.text(coord.toString().slice(0, 5), x, origin_y_px);
                    
                    // Draw minor grid lines
                    p5.strokeWeight(0.5);
                    p5.stroke(150);
                    for (let n = 1; n < minor_grid; n++) {
                        coord += (step_size / minor_grid);
                        x = (coord - x_s) * object.ppx;
                        p5.line(x, 0, x, object.height);
                    }
                }
            }
        }
        
        let x_start = Math.floor(x_s / step_size_x);
        let x_end = Math.floor(x_e / step_size_x);
        let y_start = Math.floor(y_s / step_size_y);
        let y_end = Math.floor(y_e / step_size_y);
        draw_grid_lines(step_size_x, x_start, x_end, false, this);
        draw_grid_lines(step_size_y, y_start, y_end, true, this);

        this.draw_graphs();
    }

    // Converts (x, y) from grid to location on the canvas in pixels
    __transform_coord(x, y) {
        return {x: (x - this.x_range[0]) * this.ppx, y: (-y + this.y_range[1]) * this.ppy}
    }

    draw_graphs() {
        p5.fill(255, 0, 0);
        p5.strokeWeight(2);
        
        for (let drawable of this.graphs.concat(this.scatter_plots)) {
            p5.stroke(drawable.color);
            p5.fill(drawable.color);
            this.draw_curve(drawable.compute_points(this.x_range), drawable.show_dots, drawable.show_lines);
        }

    }

    draw_curve(points, show_dots, show_lines) {
        for (let i = 0; i < points.length - 1; i++) {
            let coord = this.__transform_coord(points[i].x, points[i].y);
            if (show_dots) {
                p5.fill(points[i].color);
                p5.strokeWeight(0);
                p5.circle(coord.x, coord.y, points[i].diameter);
            }
            if (show_lines) {
                p5.strokeWeight(2);
                let coord_next = this.__transform_coord(points[i+1].x, points[i+1].y);
                p5.line(coord.x, coord.y, coord_next.x, coord_next.y);
            }
        }
    }

    // Adds the plot of some function f which outputs REAL -> REAL
    add_graph(f) {
        this.graphs.push(new Graph(f));
        this.draw();
    }

    // scatter_plot = ScatterPlot Object
    add_scatter_plot(scatter_plot) {
        this.scatter_plots.push(scatter_plot);
        this.draw();
    }

    clear_scatter_plots() {
        this.scatter_plots = [];
        this.draw();
    }

    // If amount is positive, it zooms in, otherwise zoom out
    zoom(amount) {
        // We want to multiple or divide by a number between 1 and 2 i.e. normalize
        // One wheel tick on mouse, then amount = +- 150
        // Graph this function on desmos to get a feel for its behaviour
        let normalize = (a) => {
            return -1 * Math.pow(a + 1, -0.05) + 2 
        };
        let normal = normalize(Math.abs(amount));

        if (amount > 0) {
            this.x_range[0] /= normal;
            this.x_range[1] /= normal;
            this.y_range[0] /= normal;
            this.y_range[1] /= normal;
        }
        else if (amount < 0) {
            this.x_range[0] *= normal;
            this.x_range[1] *= normal;
            this.y_range[0] *= normal;
            this.y_range[1] *= normal;
        }

        this.draw();
    }

    // Delta_x and Delta_y are percents
    move_origin(delta_x, delta_y) {
        // Make the actual change relative to domains
        delta_x *= this.x_range[1] - this.x_range[0];
        delta_y *= this.y_range[1] - this.y_range[0];

        this.x_range[0] += delta_x;
        this.x_range[1] += delta_x;
        this.y_range[0] += delta_y;
        this.y_range[1] += delta_y;

        this.draw();
    }
}
Grid.prototype.p5 = p5;

class Point {
    constructor(x, y) {
        this.x = x;
        this.y = y;
        this.diameter = 5;
        this.color = random_color();
    }
}

class Graph {
    constructor(f) {
        this.f = f;
        this.points = [];
        this.color = random_color();
        this.show_dots = false;
        this.show_lines = true;
    }

    compute_points(x_range) {
        let points_to_draw = [];

        let x_s = x_range[0];
        let x_e = x_range[1];
        let cache_distance = (x_e - x_s) / 2;

        // Find existing points which are already in the range
        for (let i = 0; i < this.points.length; i++) {
            let x = this.points[i].x;
            
            // If outside of the cache range, remove the point
            if (x < x_s - cache_distance || x > x_e + cache_distance) {
                this.points.splice(i, 1);
                i -= 1;
            }
            else if (x >= x_s || x <= x_e) {
                points_to_draw.push(this.points[i]);
            }
        }
        
        // We want 200 points to be drawn total, lets just fill in gaps
        let points_total = 200;
        let end = new Point(x_e, this.f(x_e));
        let start = new Point(x_s, this.f(x_s));
   
        points_to_draw.sort((a, b) => {
            if (a.x < b.x)
                return -1;
            else if (a.x > b.x)
                return 1;
            return 0;
        });
        points_to_draw.unshift(start);
        points_to_draw.push(end);
        this.points.push(start, end);

        let points_per_x = points_total / (x_e - x_s);
        for (let i = 0; i < points_to_draw.length - 1; i++) {
            let p0 = points_to_draw[i];
            let p1 = points_to_draw[i + 1];
            
            let points_to_fill_gap = Math.floor(points_per_x * (p1.x - p0.x));
            for (let j = points_to_fill_gap - 1; j >= 1; j--) {
                let x = (j / points_to_fill_gap * (p1.x - p0.x)) + p0.x;
                
                let point = new Point(x, this.f(x));
                this.points.push(point);
                points_to_draw.splice(i + 1, 0, point);
                i += 1;
            }
        }

        return points_to_draw;
    }
}

class ScatterPlot {
    // points = array of Point objects
    constructor(points) {
        this.color = random_color();
        this.points = points;
        this.show_dots = false;
        this.show_lines = true;
    }

    compute_points() {
        return this.points;
    }
}

/* ------------------------------------------------------------------------------------------- */
/* ----------------------------------- End of Grid ------------------------------------------- */
/* ------------------------------------------------------------------------------------------- */

/* ------------------------------------------------------------------------------------------- */
/* ----------------------------------- Main Implementation ----------------------------------- */
/* ------------------------------------------------------------------------------------------- */

var canvas_width = 750;
var canvas_height = 600;
// var aspect_ratio = canvas_height / canvas_width;
var grid = new Grid([-5, 5], [-5, 5], canvas_width, canvas_height);
var mouse_inside_canvas = false;

var canvasContainer = document.getElementById("canvasContainer");
canvasContainer.addEventListener("wheel", (event) => {
    grid.zoom(event.wheelDeltaY);
});

canvasContainer.addEventListener("mousemove", (event) => {
    // Primary button = 1
    if (event.buttons == 1) {
        grid.move_origin(event.movementX * -0.004, event.movementY * 0.004);
    }
});

canvasContainer.addEventListener("mouseenter", () => {
    mouse_inside_canvas = true;
});

canvasContainer.addEventListener("mouseleave", () => {
    mouse_inside_canvas = false;
});

// Stop scrolling if mouse is inside canvas
document.getElementById("pageContainer").addEventListener("wheel", (event) => {
    if (mouse_inside_canvas) {
        event.preventDefault();
        event.stopImmediatePropagation();
    }
});


// ib == 'Input Box' which is the container that holds an input
var inputs = [];
var ib_container = document.getElementById("inputBoxColumn");
var last_ib = document.querySelector("#lastInputBox");
var add_ib_button = document.getElementById("addColumnButton");

var error_box = document.getElementById("errorBox");
var projectile = new Projectile(0, 0, 0, 0, 0, 0, 0);

var time_start = 0;
var time_end = 20;
var num_steps = 500;

add_ib_button.addEventListener("click", () => {
    let new_ib = document.createElement("div");
    new_ib.className = "inputBox";
    
    // Swap contents of last input box and new input box
    new_ib.innerHTML = last_ib.innerHTML;
    new_ib.firstElementChild.value = last_ib.firstElementChild.value;
    last_ib.innerHTML = `<input type="text" class="mathInput" value="">`;

    ib_container.insertBefore(new_ib, last_ib);
    ib_container.scrollTop = ib_container.scrollHeight - ib_container.clientHeight;

    on_create_input(last_ib.firstElementChild);
    on_create_input(new_ib.firstElementChild);

    inputs.pop();
    inputs.push(new_ib.firstElementChild);
    inputs.push(last_ib.firstElementChild);
});


// Event handler for when a new input is created
function on_create_input(input) {
    function on_input_update(unique_input) {
        if (unique_input.value == "")
            return;
        let token_list = create_token_list(unique_input.value);
        let expression_list = token_list.slice(token_list.indexOf("=") + 1);
        
        // Update global symbol table to hold correct values
        if (token_list[0] == "F_x" || token_list[0] == "F_y") {
            symbol_table[token_list[0]] = expression_list;
            projectile.update_forces();
        }
        else {
            let token_value = parse_expression(substitute_known_symbols(expression_list));
            symbol_table[token_list[0]] = token_value;
            console.log(token_list[0], "=", token_value);

            // Update other inputs which make use of this token
            for (let other_input of inputs) {
                let search = other_input.value.search(".+=.*" + token_list[0] + ".*");
                if (search != -1) {
                    other_input.dispatchEvent(new Event("input"));
                }
            }

            projectile.update_initial_conditions();
        }
    }

    input.addEventListener("input", () => {
        try {
            on_input_update(input);
            draw_projectile();
        } catch (error) { 
            set_error_message(error);
        }
    });

    on_input_update(input);
}

function draw_projectile() {
    // Update the plot of the projectile
    grid.clear_scatter_plots();
    try {
        let points = projectile.get_points(time_start, time_end, num_steps);
        grid.add_scatter_plot(new ScatterPlot(points));
        hide_error_message();
    } catch (error) {

        set_error_message(error);
    }
}

function set_error_message(message) {
    console.log("Error: " + message);
    error_box.style.display = "block";
    error_box.innerHTML = "Error: " + message;
}

function hide_error_message() {
    error_box.style.display = "none";
}

for (let input of document.querySelectorAll(".mathInput")) {
    on_create_input(input);
    inputs.push(input);
}

draw_projectile();

}

</script>

<style scoped>
.inputBox {
    display: flex;
    padding: 25px;
    background: #fcfcfc;
    border: 0.1px solid gainsboro;
    border-bottom: none;
    border-right: none;
}

#lastInputBox {
    border-bottom: 0.1px solid gainsboro;
}

#inputBoxColumn {
    min-width: 400px;
    height: 600px;
    display: flex;
    flex-direction: column;
    overflow: auto;
    scroll-behavior: smooth;
}

#addColumnButton {
    position: absolute;
    right: 5px;
    top: -20px;
    font-size: 50px;
    -webkit-text-stroke: 1px gray;
    color: transparent;
    cursor: pointer;
    margin: 0px;
    padding: 0px;
}

#addColumnButton:hover {
    color: gray;
    -webkit-text-stroke: 0px gray;
}

.noselect {
    -webkit-touch-callout: none; /* iOS Safari */
      -webkit-user-select: none; /* Safari */
       -khtml-user-select: none; /* Konqueror HTML */
         -moz-user-select: none; /* Firefox */
          -ms-user-select: none; /* Internet Explorer/Edge */
              user-select: none; /* Non-prefixed version, currently
                                    supported by Chrome and Opera */
  }

#inputBoxColumn::-webkit-scrollbar {
    background-color:rgba(255, 255, 255, 0);
    width:10px;
}

#inputBoxColumn::-webkit-scrollbar-track {
    background-color: rgba(255, 255, 255, 0);
}

#inputBoxColumn::-webkit-scrollbar-thumb {
    background-color:#babac0;
    border-radius: 10px;
    border:4px solid #fff
}

#inputBoxColumn::-webkit-scrollbar-button {display:none}

.mathInput {
    background-color: transparent;
    border: none;
    width: 100%;
    height: 20px;
}

#errorBox {
    display: none;
    background-color: #ffd1d1;
    padding: 25px;
    border: 0.1px solid gainsboro;
}
</style>
